import _ from 'lodash';
import * as postMessage from '../privates/postMessage';
import * as members from '../privates/privateMembers';
import {types} from '../privates/utils';
import {setAsyncFunctions} from '../privates/utils/asyncDecorator';
import {setStageUnfriendlyFunctions} from '../privates/utils/livePreview';

/**
 * @mixin Node
 * @summary Provides basic functionality for all Wix objects, including objects
 *  that are not [elements]($w.Element.html).
 * @memberof $w
 */

class Node {
    constructor(RMI, compId, contextId, wixSelector, controllerId, role, type) {
        members._RMI.set(this, RMI);
        members._contextId.set(this, contextId);
        members._compId.set(this, compId);
        members._role.set(this, role);
        members._controllerId.set(this, controllerId);
        members._wixSelector.set(this, wixSelector);
        members._type.set(this, type);
    }

    /**
     * @member id
     * @label property
     * @syntax
     * get id(): string
     * @summary Gets the element's ID.
     * @description
     *  The ID is the element's unique identifier. It is used when selecting
     *  elements using the [`$w()`]($w.html#w) function.
     *
     *  An element's `id` is set in the Editor using the <a href="https://support.wix.com/en/article/how-do-i-use-the-properties-panel" target="_blank">Properties panel</a>.
     * @snippet [Node-id.es6=Get the ID]
     * @type {external:String}
     * @memberof $w.Node
     * @readonly
     */
    get id() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        return RMI.getId(compId);
    }

    /*
     * --- THIS METHOD IS INTENTIONALLY UNDOCUMENTED ---
     * @member connectionConfig
     * @label property
     * @syntax
     * let controllerConfig = $w("#myController").connectionConfig;
     * @summary Gets the controller's config.
     * @attr {external:String}
     * @config {external:String}
     * @memberof $w.Node
     * @readonly
     */
    get connectionConfig() {
        const compId = members._compId.get(this);
        const role = members._role.get(this);
        const RMI = members._RMI.get(this);
        return RMI.getConfig(compId, role);
    }

    get uniqueId() {
        return members._compId.get(this);
    }

    /**
     * @member parent
     * @label property
     * @syntax
     * get parent(): Node
     * @summary Gets the element's parent element.
     * @description
     *  Some elements can contain other elements. This occurs when you drag
     *  an element onto a container element. The container is the `parent` of
     *  all the elements it contains.
     *
     *  [`Page`]($w.Page.html), [`Header`]($w.Header.html), and
     *  [`Footer`]($w.Footer.html) are top-level elements and have no parent.
     * @snippet [Node-parent.es6=Get the parent element and the parent's ID]
     * @type {$w.Node}
     * @default null
     * @see [children]($w.ContainableMixin.html#children)
     * @memberof $w.Node
     * @readonly
     */
    get parent() {
        const RMI = members._RMI.get(this);
        const wixSelector = members._wixSelector.get(this);
        const compId = members._compId.get(this);
        const controllerId = members._controllerId.get(this);

        if (_.isUndefined(compId) || compId === 'default') {
            return;
        }

        const parentId = RMI.getParent(compId);
        const parent = RMI.getComp(parentId);
        if (_.isUndefined(parent) || _.isNull(parent)) {
            return;
        }

        const appDefId = RMI.getAppDefinitionId();
        const connectedCompIds = RMI.getAllCompIdsInScope();
        if (_.isUndefined(appDefId) || appDefId === 'dataBinding' || _.includes(connectedCompIds, parentId)) {
            return wixSelector.getInstance(RMI, parent.type, parentId, controllerId);
        }
        return;
    }

    /**
     * @member global
     * @label property
     * @syntax
     * get global(): boolean
     * @summary Indicates if an element appears on all pages or only on the current page.
     * @description
     *  If `global` is `true`, the element appears on all pages.
     *
     *  If `global` is `false`, the element only appears on the current page.
     * @snippet [Node-global_get.es6=Get whether an element is displayed on all pages]
     * @type {external:Boolean}
     * @default false
     * @memberof $w.Node
     * @readonly
     */
    get global() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const MASTER_PAGE_ID = "masterPage";
        let rootId = null;
        let parentId = compId;

        do {
            parentId = RMI.getParent(parentId);
            rootId = parentId || rootId;
        } while (parentId);

        return !rootId || rootId === MASTER_PAGE_ID;
    }

    /**
     * @member type
     * @label property
     * @syntax
     * get type(): string
     * @summary Gets the element's type.
     * @snippet [Node-type.es6=Get the element's type]
     * @type {external:String}
     * @memberof $w.Node
     * @readonly
     */
    get type() {
        const type = members._type.get(this);
        if (_.isObject(type)) {
            const compType = type.compType && type.compType !== types.getDefaultSdkType() ? type.compType : _.last(type.viewerType.split('.'));
            return `$w.${compType}`;
        }
        return `$w.${type}`;

    }

    /**
     * @function scrollTo
     * @syntax
     * function scrollTo(): Promise<void>
     * @summary Scrolls the page to the element using an animation.
     * @description
     *  The `scrollTo()` function returns a Promise that is resolved when the
     *  animated scroll is complete and the element is now in view.
     *
     *  To scroll to a specific location on the page, see the `wix-window`
     *  [`scrollTo()`](wix-window.html#scrollTo) function.
     *
     *  Calling the `scrollTo()` function on an element in a repeated item that
     *  is selected from the [global scope]($w.Repeater.html#global-scope) causes
     *  an error.
     * @snippet [Node-scrollTo.es6=Scroll the page to an element]
     * @snippet [Node-scrollTo_then.es6=Scroll the page to an element and log message when done]
     * @returns {Promise}
     * @fulfill {void} When the scroll is complete.
     * @memberof $w.Node
     */
    scrollTo() {
        return new Promise((resolve, reject) => {
            const compId = members._compId.get(this);
            const RMI = members._RMI.get(this);
            const rendered = RMI.getComp(compId).isDisplayed;
            if (rendered) {
                const callback = (data, error) => { return error ? reject(error) : resolve(data); };
                postMessage.sendMessageToHost(postMessage.Intent.WIX_CODE_SITE_API, {
                    command: postMessage.MessageTypes.SCROLL_TO_COMP,
                    data: {compId, requiresDom: {compId}},
                    controllerId: members._controllerId.get(this),
                    callback
                });
            } else {
                reject({error: 'component is not rendered thus can\'t be scrollTo'});
            }
        });
    }

    toJSON() {
        const {id, type, global} = this;
        return {id, type, global};
    }

}

setAsyncFunctions('scrollTo');
setStageUnfriendlyFunctions(Node, 'scrollTo');

export default Node;
